import { useEffect, useState } from "react";
import "@/App.css";
import {
  ArrowUpRight,
  Mail,
  MessageSquare,
  Apple,
  Smartphone,
  Building2,
  GraduationCap,
  MapPin,
} from "lucide-react";

const PITCH_DECK_URL =
  "https://docs.google.com/presentation/d/1ohXKv9BXvJanSkQY41UtBIgyDY4k4PnS5Oq2XPVAOpg/edit?usp=sharing";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_bike-garage-promo/artifacts/jmzco1h0_2B71DE8E-F6F7-423E-B88C-6731BD9F71D2.jpeg";

const SECTIONS = [
  { id: "app", label: "Get the App" },
  { id: "invest", label: "For Cities" },
  { id: "gallery", label: "Gallery" },
  { id: "videos", label: "Videos" },
  { id: "start", label: "Contact" },
];

const GALLERY_PHOTOS = [
  {
    src: "https://customer-assets.emergentagent.com/job_bike-garage-promo/artifacts/jmzco1h0_2B71DE8E-F6F7-423E-B88C-6731BD9F71D2.jpeg",
    alt: "Public Bike Garage units 01 and 02 at a beachfront parking lot with palm trees",
    caption: "Units 01 & 02 — Beachfront",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_bike-garage-promo/artifacts/toe50rp0_IMG_1632.JPG",
    alt: "A rider standing with an e-bike next to a Public Bike Garage along the boardwalk",
    caption: "On the Boardwalk — Rider & Unit",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_bike-garage-promo/artifacts/02z9xhd9_B4577CD2-7B88-4C1A-AF3A-6F839798D35D.jpeg",
    alt: "Public Bike Garage units at golden hour with palm tree and parking lot",
    caption: "Open-Air Install — Palm & Lot",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_bike-garage-promo/artifacts/3694qslw_IMG_1634.JPG",
    alt: "Top-down view of a Public Bike Garage at golden hour with palms in the distance",
    caption: "Rooftop Detail — Golden Hour",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_bike-garage-promo/artifacts/2aztac4p_B5A67496-2FDA-4F28-B5DC-47990A8FFCE5.jpeg",
    alt: "Close-up of Public Bike Garage doors with bike signage at golden hour",
    caption: "Detail — Wood, Steel, Signage",
  },
];

const VIDEOS = [
  {
    id: "1orEkt1UWasCrtngnNJCSlOczuB4Du85R",
    title: "Bike Garage — Overview",
  },
  {
    id: "1pfhtm5gUHC6olK_3Uu2JTASxaqrfEo73",
    title: "On Location",
  },
  {
    id: "1cxYjslx8E0PclukCJkNeu6THGSu7PS8b",
    title: "In Action",
  },
];

function NavBar() {
  return (
    <header
      data-testid="site-nav"
      className="absolute top-0 left-0 right-0 z-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 flex items-center justify-between py-6">
        <a href="#top" className="flex items-center gap-3" data-testid="brand-link">
          <span className="accent-dot" />
          <span className="serif text-xl tracking-tight text-white drop-shadow-sm">
            Public Bike Garage
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`nav-${s.id}`}
              className="text-sm text-white/90 hover:text-white transition-colors drop-shadow-sm"
            >
              {s.label}
            </a>
          ))}
        </nav>
        <a
          href="#app"
          data-testid="nav-cta"
          className="hidden md:inline-flex btn-ghost btn-ghost-onDark"
          style={{ padding: "10px 18px", fontSize: 13 }}
        >
          Download App
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative">
      <div className="hero-media">
        <img
          src={HERO_IMAGE}
          alt="A Public Bike Garage installation at a beachfront parking lot"
          className="hero-img"
        />
        <div className="hero-scrim" />
      </div>

      <div
        id="app"
        className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 min-h-[100svh] flex items-start pt-16 sm:pt-20 lg:pt-24 pb-16 sm:pb-24"
      >
        <div className="grid grid-cols-12 gap-8 w-full">
          <div className="col-span-12 lg:col-span-9 fade-up">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline hairline-light" />
              <span className="eyebrow eyebrow-light">The Bike Garage · Now Open</span>
            </div>

            <h1
              className="serif text-white text-[40px] sm:text-[64px] lg:text-[86px] leading-[0.98] tracking-tight max-w-[1100px]"
              data-testid="hero-title"
            >
              Download the Bike Garage App to access a
              <span className="italic font-medium"> bike garage near you.</span>
            </h1>

            <p className="mt-6 max-w-[620px] text-white/85 text-base sm:text-lg leading-relaxed">
              Locate the closest secure bike, e-bike, scooter, and skateboard garage,
              reserve a stall in seconds, and unlock it from your phone.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#"
                data-testid="hero-ios-button"
                onClick={(e) => e.preventDefault()}
                aria-label="Download on the App Store (coming soon)"
                className="store-btn store-btn-hero"
              >
                <Apple size={28} strokeWidth={1.5} />
                <span>
                  <span className="store-sub">Download for</span>
                  <span className="store-main">iPhone</span>
                </span>
              </a>
              <a
                href="#"
                data-testid="hero-android-button"
                onClick={(e) => e.preventDefault()}
                aria-label="Get it on Google Play (coming soon)"
                className="store-btn store-btn-hero"
              >
                <Smartphone size={28} strokeWidth={1.5} />
                <span>
                  <span className="store-sub">Download for</span>
                  <span className="store-main">Android</span>
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Investors() {
  return (
    <section
      id="invest"
      data-testid="invest-section"
      className="bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28">
        <div className="grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 md:col-span-3">
            <div className="section-num">02 — For Cities & Campuses</div>
          </div>

          <div className="col-span-12 md:col-span-9">
            <h2
              className="serif text-[36px] sm:text-[52px] lg:text-[68px] leading-[1.02]"
              data-testid="invest-heading"
            >
              Is your city, campus, or university interested in{" "}
              <span className="italic font-medium">incorporating the Bike Garage?</span>
            </h2>

            <p className="mt-8 text-[var(--ink-soft)] text-lg max-w-[720px]">
              We partner with municipalities, transit authorities, universities, and property
              owners to install secure micro-mobility parking where people already ride.
              Modular and fast to deploy.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-[900px]">
              <div className="p-6 border border-[var(--line)] rounded-md" data-testid="invest-tile-cities">
                <Building2 size={22} strokeWidth={1.5} />
                <div className="mt-4 serif text-2xl">Cities</div>
                <div className="mt-2 text-sm text-[var(--muted)]">
                  Beachfronts, boardwalks, transit stops, downtown corridors.
                </div>
              </div>
              <div className="p-6 border border-[var(--line)] rounded-md" data-testid="invest-tile-campuses">
                <GraduationCap size={22} strokeWidth={1.5} />
                <div className="mt-4 serif text-2xl">Universities</div>
                <div className="mt-2 text-sm text-[var(--muted)]">
                  Residence halls, quads, athletic centers, commuter lots.
                </div>
              </div>
              <div className="p-6 border border-[var(--line)] rounded-md" data-testid="invest-tile-props">
                <MapPin size={22} strokeWidth={1.5} />
                <div className="mt-4 serif text-2xl">Properties</div>
                <div className="mt-2 text-sm text-[var(--muted)]">
                  Retail centers, office parks, apartments, hotels.
                </div>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap gap-3 items-center">
              <a
                href={PITCH_DECK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="invest-find-out-more"
                className="btn-ink"
              >
                Find out more
                <span className="btn-arrow"><ArrowUpRight size={16} /></span>
              </a>
              <a
                href="#start"
                data-testid="invest-contact-link"
                className="ink-link text-sm text-[var(--muted)]"
              >
                Or contact us directly
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const words = [
    "Fast to Deploy",
    "Built for Riders",
    "Fits in a Parking Spot",
    "3 Bays Per Unit",
    "Pays for Itself",
    "Turn-Key, No Maintenance",
  ];
  const items = [...words, ...words, ...words];
  return (
    <div className="marquee" data-testid="marquee">
      <div className="marquee-track">
        {items.map((w, i) => (
          <span key={i} className="inline-flex items-center gap-12">
            {w}
            <span className="dot">●</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function GalleryFigure({ photo, index, total, colSpan, aspect }) {
  return (
    <figure
      className={`${colSpan} group overflow-hidden rounded-md border border-[var(--line)]`}
      data-testid={`gallery-photo-${index}`}
    >
      <div className="overflow-hidden">
        <img
          src={photo.src}
          alt={photo.alt}
          loading="lazy"
          className={`w-full h-auto ${aspect} object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]`}
        />
      </div>
      <figcaption className="flex items-center justify-between px-5 py-4 text-xs uppercase tracking-[0.18em] text-[var(--muted)]">
        <span>{photo.caption}</span>
        <span className="section-num">
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
      </figcaption>
    </figure>
  );
}

function Gallery() {
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28 border-t border-[var(--line)]"
    >
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-3">
          <div className="section-num">03 — Gallery</div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h2 className="serif text-[36px] sm:text-[52px] lg:text-[68px] leading-[1.02]" data-testid="gallery-heading">
            Bike Garage<br />Photo Gallery
          </h2>
          <p className="mt-6 text-[var(--ink-soft)] text-lg max-w-[640px]">
            Real units, in real places. A look at Public Bike Garage in the wild.
          </p>

          <div className="mt-12 grid grid-cols-12 gap-5">
            <GalleryFigure photo={GALLERY_PHOTOS[0]} index={0} total={GALLERY_PHOTOS.length} colSpan="col-span-12 md:col-span-8" aspect="aspect-[4/3]" />
            <GalleryFigure photo={GALLERY_PHOTOS[1]} index={1} total={GALLERY_PHOTOS.length} colSpan="col-span-12 md:col-span-4" aspect="aspect-[4/5]" />
            <GalleryFigure photo={GALLERY_PHOTOS[2]} index={2} total={GALLERY_PHOTOS.length} colSpan="col-span-12 md:col-span-6" aspect="aspect-square" />
            <GalleryFigure photo={GALLERY_PHOTOS[3]} index={3} total={GALLERY_PHOTOS.length} colSpan="col-span-12 md:col-span-6" aspect="aspect-square" />
            <GalleryFigure photo={GALLERY_PHOTOS[4]} index={4} total={GALLERY_PHOTOS.length} colSpan="col-span-12" aspect="aspect-[16/9]" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Videos() {
  return (
    <section
      id="videos"
      data-testid="videos-section"
      className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28 border-t border-[var(--line)]"
    >
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-3">
          <div className="section-num">04 — Watch</div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h2 className="serif text-[36px] sm:text-[52px] lg:text-[68px] leading-[1.02]" data-testid="videos-heading">
            Bike Garage<br />Videos
          </h2>
          <p className="mt-6 text-[var(--ink-soft)] text-lg max-w-[640px]">
            Walkthroughs and behind-the-scenes footage of Public Bike Garage in the wild.
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VIDEOS.map((v, i) => (
              <figure
                key={v.id}
                className="overflow-hidden rounded-md border border-[var(--line)] bg-[#0a0a0a]"
                data-testid={`video-${i}`}
              >
                <div className="relative w-full aspect-[9/16]">
                  <iframe
                    src={`https://drive.google.com/file/d/${v.id}/preview`}
                    title={v.title}
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    loading="lazy"
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 0 }}
                  />
                </div>
                <figcaption className="flex items-center justify-between px-5 py-4 text-xs uppercase tracking-[0.18em] text-white/70 bg-[#0a0a0a]">
                  <span>{v.title}</span>
                  <span className="section-num" style={{ color: "rgba(255,255,255,0.55)" }}>
                    {String(i + 1).padStart(2, "0")} / {String(VIDEOS.length).padStart(2, "0")}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function GetStarted() {
  const email = "john@publicbikegarage.com";
  const phone = "3364040037";
  const phoneDisplay = "(336) 404-0037";

  const [copied, setCopied] = useState(null);
  const copy = async (val, key) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(key);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      setCopied(null);
    }
  };

  return (
    <section
      id="start"
      data-testid="start-section"
      className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 sm:py-28 border-t border-[var(--line)]"
    >
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-3">
          <div className="section-num">05 — Talk to Us</div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h2 className="serif text-[44px] sm:text-[64px] lg:text-[88px] leading-[1.0]" data-testid="start-heading">
            Get in touch<br /><span className="italic font-medium">now.</span>
          </h2>
          <p className="mt-6 text-[var(--ink-soft)] text-lg max-w-[640px]">
            Email or text. That&apos;s it. We&apos;ll answer questions and walk you through
            bringing a Bike Garage to your location.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[820px]">
            <a
              href={`mailto:${email}`}
              data-testid="contact-email"
              className="group block p-7 border border-[var(--line)] rounded-md hover:border-[var(--ink)] transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-[var(--muted)] text-xs uppercase tracking-[0.18em]">
                  <Mail size={14} /> Email
                </div>
                <ArrowUpRight size={18} className="text-[var(--muted)] group-hover:text-[var(--ink)] transition-colors" />
              </div>
              <div className="serif text-2xl sm:text-[28px] mt-5 break-all">{email}</div>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); copy(email, "email"); }}
                data-testid="contact-email-copy"
                className="mt-5 text-xs uppercase tracking-[0.18em] text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
              >
                {copied === "email" ? "Copied ✓" : "Copy address"}
              </button>
            </a>

            <a
              href={`sms:+1${phone}`}
              data-testid="contact-sms"
              className="group block p-7 border border-[var(--line)] rounded-md hover:border-[var(--ink)] transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-[var(--muted)] text-xs uppercase tracking-[0.18em]">
                  <MessageSquare size={14} /> Text
                </div>
                <ArrowUpRight size={18} className="text-[var(--muted)] group-hover:text-[var(--ink)] transition-colors" />
              </div>
              <div className="serif text-2xl sm:text-[28px] mt-5">{phoneDisplay}</div>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); copy(phone, "phone"); }}
                data-testid="contact-sms-copy"
                className="mt-5 text-xs uppercase tracking-[0.18em] text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
              >
                {copied === "phone" ? "Copied ✓" : "Copy number"}
              </button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-[var(--line)] mt-12">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <span className="accent-dot" />
          <span className="serif text-lg">Public Bike Garage</span>
        </div>
        <div className="foot">
          © {new Date().getFullYear()} Public Bike Garage · Built for owners and riders.
        </div>
        <div className="foot uppercase tracking-[0.2em]">
          <a href="#start" className="ink-link" data-testid="footer-start-link">Get in touch →</a>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  useEffect(() => {
    document.title = "Public Bike Garage — Download the App";
  }, []);

  return (
    <div className="min-h-screen bg-white text-[var(--ink)]" data-testid="app-root">
      <Hero />
      <Marquee />
      <Investors />
      <Gallery />
      <Videos />
      <GetStarted />
      <Footer />
    </div>
  );
}
